import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  PaymentMethodPicker as SharedPaymentMethodPicker,
  type PaymentMethodPickerProps,
  type PaymentMethodPickerRenderModel,
} from "@bw/shared/components";

type WithoutRender<T> = T extends unknown ? Omit<T, "render"> : never;

export type MaterialPaymentMethodPickerProps =
  WithoutRender<PaymentMethodPickerProps>;

function PaymentMethodPickerView({
  announcement,
  radioGroupName,
  empty,
  setEmptyElement,
  setGroupElement,
  rows,
}: PaymentMethodPickerRenderModel) {
  return (
    <>
      <Box
        aria-live="polite"
        sx={{
          position: "absolute",
          width: "1px",
          height: "1px",
          p: 0,
          m: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {announcement}
      </Box>

      {empty ? (
        <Typography
          ref={setEmptyElement}
          tabIndex={-1}
          color="text.secondary"
          variant="body2"
        >
          No saved cards yet — add one below to continue.
        </Typography>
      ) : (
        <Stack
          ref={setGroupElement}
          role="radiogroup"
          aria-label="Choose a saved card"
          tabIndex={-1}
          spacing={1}
        >
          {rows.map((row) => (
            <Box
              key={row.method.payment_method_id}
              data-selected={row.selected || undefined}
              data-disabled={row.disabled || undefined}
              sx={{
                border: 1,
                borderColor: row.selected ? "primary.main" : "divider",
                borderRadius: 2,
                p: 1.5,
                opacity: row.disabled ? 0.72 : 1,
                bgcolor: row.selected ? "action.selected" : "transparent",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ alignItems: { xs: "stretch", sm: "center" } }}
              >
                <FormControlLabel
                  sx={{
                    m: 0,
                    minWidth: 0,
                    flex: 1,
                    alignItems: "flex-start",
                    ".MuiFormControlLabel-label": { minWidth: 0, flex: 1 },
                  }}
                  control={
                    <Radio
                      name={radioGroupName}
                      checked={row.selected}
                      onChange={row.select}
                      disabled={row.disabled}
                      slotProps={{
                        input: { "aria-label": row.accessibleLabel },
                      }}
                    />
                  }
                  label={
                    <Stack spacing={0.25} sx={{ pt: 0.75 }}>
                      <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{ alignItems: "center", flexWrap: "wrap" }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>
                          {row.method.brand} ···· {row.method.last4}
                        </Typography>
                        {row.method.is_default ? (
                          <Chip
                            label="Default"
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        ) : null}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {row.expiry
                          ? `Expires ${row.expiry}`
                          : "Expiry unavailable"}
                      </Typography>
                      {!row.method.is_usable && row.unavailableLabel ? (
                        <Typography variant="body2" color="warning.main">
                          {row.unavailableLabel}. Add a new card below.
                        </Typography>
                      ) : null}
                      {row.uncertain ? (
                        <Typography variant="body2" color="warning.main">
                          Status unavailable. Use another card.
                        </Typography>
                      ) : null}
                    </Stack>
                  }
                />

                {row.canRemove ? (
                  <Button
                    ref={row.setRemoveButton}
                    type="button"
                    variant="outlined"
                    color="inherit"
                    size="small"
                    aria-label={row.removeLabel}
                    onClick={row.beginRemove}
                    disabled={row.removeDisabled}
                  >
                    Remove
                  </Button>
                ) : null}
              </Stack>

              {row.errorMessage ? (
                <Alert severity="error" role="alert" sx={{ mt: 1 }}>
                  {row.errorMessage}
                </Alert>
              ) : null}

              {row.canRemove && row.confirming ? (
                <Box
                  sx={{
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2">
                    Remove {row.cardName}? This won&apos;t cancel your existing
                    bookings or enrollments — it only deletes the saved card.
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1, flexWrap: "wrap" }}
                  >
                    <Button
                      ref={row.setConfirmButton}
                      type="button"
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={row.confirmRemove}
                      disabled={row.confirmDisabled}
                    >
                      {row.removing ? "Removing…" : "Confirm removal"}
                    </Button>
                    <Button
                      type="button"
                      variant="text"
                      color="inherit"
                      size="small"
                      onClick={row.keepCard}
                      disabled={row.keepDisabled}
                    >
                      Keep card
                    </Button>
                  </Stack>
                </Box>
              ) : null}
            </Box>
          ))}
        </Stack>
      )}
    </>
  );
}

/** Player-native MUI presentation over the shared payment-method controller. */
export function MaterialPaymentMethodPicker(
  props: MaterialPaymentMethodPickerProps,
) {
  return (
    <SharedPaymentMethodPicker
      {...props}
      render={(model) => <PaymentMethodPickerView {...model} />}
    />
  );
}
